// SPDX-License-Identifier: MIT
// Presentational "ton appli en ligne" card (mockup carnet-parcours.html). Two modes:
//   • deployStatus null/absent → the HONEST "bientôt" state (subdomain deploy not
//     yet triggered): a projected address, "Voir en ligne" disabled. Shipped default.
//   • deployStatus set (deploy_frontend) → REAL states: `live` (enabled "Voir en
//     ligne" opening <handle>.genesis-platform.fr), `building` (honest progress),
//     `failed` (message + log detail). Cross-platform (web AND desktop).
// "Télécharger mon code" wires zip.ts; "Domaine perso" → /tarifs.
import { t } from '../i18n/t';
import { escapeHtml } from '../lib/escape';
import { shortHandle } from '../projects/handle';

export interface OnlinePreviewOptions {
  title: string; // project title → slug for the projected URL (bientôt mode)
  createdAt?: string;
  // deploy_frontend state (from the project row). Absent/null → "bientôt".
  deployStatus?: 'building' | 'live' | 'failed' | null;
  deployUrl?: string | null;
  logTail?: string; // shown under the failed state for debugging
  onDownload: () => void | Promise<void>; // wires zip.ts
  onProDomain: () => void; // → /tarifs
  // Deploy-expiry countdown/banner (Part B2). Only surfaced in the `live` state.
  // ISO-8601 real expiry (from `deploy_expires_at`) → drives expiryState(). null/
  // absent while live = Cloud-permanent = NO countdown.
  deployExpiresAt?: string | null;
  onRedeploy?: () => void; // → re-runs the deploy-frontend path (resets +14 j)
  onKeepOnline?: () => void; // → Cloud 5€ offer (only when cloudEnabled)
  cloudEnabled?: boolean; // CP-3 gate; false → "bientôt" affordance (never a dead-end)
}

// Pure escalation state for the deploy-expiry countdown/banner (Part B1). `null`/
// `undefined`/empty → 'none' (never-deployed OR Cloud-permanent = no countdown).
// daysLeft = ceil((expires − now) / 1 day), clamped to >= 0. tier = 'alert' at
// daysLeft <= 3, else 'calm'. Unparseable dates are treated defensively as 'none'
// rather than throwing.
export function expiryState(
  deployExpiresAt: string | null | undefined,
  now: number,
): { tier: 'none' | 'calm' | 'alert'; daysLeft: number } {
  if (!deployExpiresAt) return { tier: 'none', daysLeft: 0 };
  const expiresMs = Date.parse(deployExpiresAt);
  if (Number.isNaN(expiresMs)) return { tier: 'none', daysLeft: 0 };
  const daysLeft = Math.max(0, Math.ceil((expiresMs - now) / 86_400_000));
  return { tier: daysLeft <= 3 ? 'alert' : 'calm', daysLeft };
}

// Derive a URL-safe slug from a project title: strip accents + emoji, lowercase,
// hyphenate. Falls back to "mon-app" when nothing usable remains.
export function slugify(title: string): string {
  const s = title
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '') // combining accents
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return s || 'mon-app';
}

export function renderOnlinePreview(opts: OnlinePreviewOptions): HTMLElement {
  const root = document.createElement('section');
  root.className = 'online-preview';
  root.setAttribute('data-component', 'online-preview');

  const status = opts.deployStatus ?? null;
  const liveUrl = opts.deployUrl ?? null;
  // shortHandle, NOT the raw slugify: the projected subdomain must match the
  // wizard's prefill (2 meaningful words, ≤24 chars). A 56-char projected URL
  // reads as the committed name (operator feedback 2026-06-11 « nom à rallonge »).
  const projected = `${shortHandle(opts.title)}${t('onlinePreview.urlSuffix')}`;
  // Show the REAL address once we have one (live/building/failed); else the projected one.
  const shownUrl = (status && liveUrl) ? liveUrl.replace(/^https?:\/\//, '') : projected;
  root.setAttribute('data-deploy-status', status ?? 'none');

  const heading = status === 'live' ? t('onlinePreview.liveHeading')
    : status === 'building' ? t('onlinePreview.buildingHeading')
    : status === 'failed' ? t('onlinePreview.failedHeading')
    : t('onlinePreview.heading');

  // Escalation is computed early because it shapes the whole live layout:
  // the alert demotes « Voir en ligne » and both tiers replace the status line.
  const { tier, daysLeft } = status === 'live'
    ? expiryState(opts.deployExpiresAt, Date.now())
    : { tier: 'none' as const, daysLeft: 0 };

  // The "Voir en ligne" affordance: a real enabled <a> when live, else a disabled
  // button. Near expiry (alert) the link demotes to ghost so « Redéployer » is the
  // single dominant CTA — demoted, never disabled (the site IS still online).
  const viewLiveClass = tier === 'alert'
    ? 'online-preview__btn online-preview__btn--ghost'
    : 'online-preview__btn online-preview__btn--live';
  const viewLiveHtml = (status === 'live' && liveUrl)
    ? `<a data-component="online-view-live" class="${viewLiveClass}" href="${escapeHtml(liveUrl)}" target="_blank" rel="noopener noreferrer">${escapeHtml(t('onlinePreview.viewLive'))}</a>`
    : `<button type="button" data-component="online-view-live" class="online-preview__btn online-preview__btn--live" disabled title="${escapeHtml(t('onlinePreview.phase3Note'))}">${escapeHtml(t('onlinePreview.viewLiveSoon'))}</button>`;

  // Status line under the URL. When a countdown renders (tier ≠ none), the
  // static live note gives way entirely — the countdown line carries the live
  // confirmation, the free window AND the Cloud hint in ONE line (never two
  // stacked status lines saying the same thing).
  let statusLineHtml = `<p class="online-preview__free">⏳ <span>${escapeHtml(t('onlinePreview.freeNote'))}</span></p>`;
  if (status === 'live') {
    statusLineHtml = tier === 'none'
      ? `<p class="online-preview__free" data-component="online-live">✓ <span>${escapeHtml(t('onlinePreview.liveNote'))}</span></p>`
      : '';
  } else if (status === 'building') {
    statusLineHtml = `<p class="online-preview__free online-preview__free--building" data-component="online-building"><span class="online-preview__spin" aria-hidden="true"></span> <span>${escapeHtml(t('onlinePreview.building'))}</span></p>`;
  } else if (status === 'failed') {
    statusLineHtml = `<p class="online-preview__free online-preview__free--failed" data-component="online-failed">⚠️ <span>${escapeHtml(t('onlinePreview.failedNote'))}</span></p>`
      + (opts.logTail ? `<details class="online-preview__log"><summary>${escapeHtml(t('onlinePreview.viewLog'))}</summary><pre>${escapeHtml(opts.logTail)}</pre></details>` : '');
  }

  // ── Deploy-expiry countdown / escalating banner ──────────────────────
  // Only in the `live` state. expiryState() returns 'none' for null/unparseable
  // expiry (Cloud-permanent) → nothing extra renders.
  // calm: a subtle countdown line only — no action buttons (the card's existing
  //   actions already cover download/Cloud; a calm 14-day window is not urgent).
  // alert (<=3 days left): the escalated banner + exactly two actions
  //   (Redéployer, Garder en ligne · Cloud). No download button here — the
  //   card's own "Télécharger mon code" already covers it, and duplicating it
  //   would just clutter an already-urgent moment.
  let expiryHtml = '';
  // Near expiry, "Garder en ligne (Cloud)" supersedes the card's generic
  // "Domaine perso (Cloud)" — never show two Cloud buttons at once.
  let hideProDomain = false;
  if (status === 'live') {
    if (tier !== 'none') {
      const lineKey = tier === 'alert'
        ? (daysLeft === 0 ? 'onlinePreview.expiryToday'
          : daysLeft === 1 ? 'onlinePreview.expiryAlertOne'
          : 'onlinePreview.expiryAlert')
        : (daysLeft === 1 ? 'onlinePreview.expiryCalmOne' : 'onlinePreview.expiryCalm');
      const lineText = t(lineKey).replace('{n}', String(daysLeft));

      if (tier === 'alert') {
        hideProDomain = true;
        // Garder en ligne : a real Cloud offer when enabled, else a disabled
        // "bientôt" affordance — informative, never a dead-end.
        const keepOnlineHtml = opts.cloudEnabled
          ? `<button type="button" data-component="online-keep-online" class="online-preview__btn online-preview__btn--ghost online-preview__btn--keep">${escapeHtml(t('onlinePreview.expiryKeepOnline'))}</button>`
          : `<button type="button" data-component="online-keep-online" class="online-preview__btn online-preview__btn--ghost online-preview__btn--keep" disabled title="${escapeHtml(t('onlinePreview.expiryCloudSoon'))}">${escapeHtml(t('onlinePreview.expiryCloudSoon'))}</button>`;

        expiryHtml = `
    <div class="online-preview__expiry online-preview__expiry--alert" data-component="online-expiry" data-tier="alert">
      <p class="online-preview__expiry-line">${escapeHtml(lineText)}</p>
      <div class="online-preview__expiry-actions">
        <button type="button" data-component="online-redeploy" class="online-preview__btn online-preview__btn--redeploy">${escapeHtml(t('onlinePreview.expiryRedeploy'))}</button>
        ${keepOnlineHtml}
      </div>
    </div>`;
      } else {
        // Calm: countdown line only, no action buttons.
        expiryHtml = `
    <div class="online-preview__expiry" data-component="online-expiry" data-tier="calm">
      <p class="online-preview__expiry-line">${escapeHtml(lineText)}</p>
    </div>`;
      }
    }
  }

  const proDomainHtml = hideProDomain ? '' : `<button type="button" data-component="online-pro-domain" class="online-preview__btn online-preview__btn--ghost">${escapeHtml(t('onlinePreview.proDomain'))}</button>`;

  root.innerHTML = `
    <h3 class="online-preview__heading">${escapeHtml(heading)}</h3>
    <span class="online-preview__url-label">${escapeHtml(status === 'live' ? t('onlinePreview.liveUrlLabel') : t('onlinePreview.urlLabel'))}</span>
    <span class="online-preview__url">${escapeHtml(shownUrl)} ↗</span>
    ${statusLineHtml}
    ${expiryHtml}
    <div class="online-preview__actions">
      ${viewLiveHtml}
      <button type="button" data-component="online-download" class="online-preview__btn online-preview__btn--ghost">${escapeHtml(t('onlinePreview.download'))}</button>
      ${proDomainHtml}
    </div>
  `;

  root.querySelector<HTMLButtonElement>('[data-component="online-download"]')!
    .addEventListener('click', () => { void opts.onDownload(); });
  root.querySelector<HTMLButtonElement>('[data-component="online-pro-domain"]')
    ?.addEventListener('click', () => { opts.onProDomain(); });

  // Deploy-expiry actions (present only when the alert banner rendered).
  root.querySelector<HTMLButtonElement>('[data-component="online-redeploy"]')
    ?.addEventListener('click', () => { opts.onRedeploy?.(); });
  root.querySelector<HTMLButtonElement>('[data-component="online-keep-online"]')
    ?.addEventListener('click', () => { opts.onKeepOnline?.(); });

  return root;
}
